
import {
  computed,
  defineComponent,
  onBeforeUpdate,
  PropType,
  reactive,
  ref,
} from "vue";
import { EditStore } from "@/stores";
import EditDownloadSelectSize, {
  IDownloadSize,
  SizeMode,
} from "@/components/EditDownloadSelectSize.vue";
import EditDownloadSelectOutput from "@/components/EditDownloadSelectOutput.vue";
import EditDownloadPreview from "@/components/EditDownloadPreview.vue";
import { useDownloader } from "@/lib/downloader";
import {
  DisplayOrder,
  IOutputStyle,
  OrderStyle,
  OutputType,
  ViewOrder,
} from "@/services/Models";
import { flatPromise } from "@/lib/shared";
import { useLoading } from "@/lib/loading";
import { consts } from "../services/Models";

enum Pages {
  Step1,
  Step2,
}

const notDownload = "/orders/notdownload.png";

// 編集後画像と編集前画像を持つオブジェクト
// 編集後画像は使う時に作る
export class ImageFrame {
  template: HTMLImageElement;
  edited: () => Promise<HTMLImageElement>;
  _cache: HTMLImageElement | undefined;

  constructor(
    template: HTMLImageElement,
    makeEdited: () => Promise<HTMLImageElement>
  ) {
    this.template = template;
    this.edited = async () => {
      if (this._cache === undefined) {
        this._cache = await makeEdited();
      }
      return this._cache;
    };
  }
}

// プレビュー画像を持つオブジェクト
export class ImagePreview {
  size: IDownloadSize | undefined;
  outputStyle: IOutputStyle | undefined;
  dataURIs: Map<string, string> = new Map<string, string>();
  images = [] as string[];

  init = () => {
    this.size = undefined;
    this.outputStyle = undefined;
    this.dataURIs = new Map<string, string>();
    this.images = [];
  };
  set(
    size: IDownloadSize,
    outputStyle: IOutputStyle,
    dataURIs: Map<string, string>
  ) {
    let json = JSON.stringify(size);
    this.size = JSON.parse(json) as IDownloadSize;
    json = JSON.stringify(outputStyle);
    this.outputStyle = JSON.parse(json) as IOutputStyle;
    this.dataURIs = dataURIs;
    this.setImages();
  }
  isSame = (size: IDownloadSize, outputStyle: IOutputStyle): boolean => {
    let same = false;
    same =
      JSON.stringify(this.size) === JSON.stringify(size) &&
      JSON.stringify(this.outputStyle) === JSON.stringify(outputStyle);
    return same;
  };
  setImages = () => {
    this.images = [];
    const order = [1, 3, 0, 2];
    for (const [index, dataURL] of Array.from(
      this.dataURIs.values()
    ).entries()) {
      switch (this.outputStyle?.outputType) {
        // 縦に連結
        // 2x2でダウンロード
        case OutputType.vAlign:
        case OutputType.twoBytwo:
          this.images[0] = dataURL;
          break;
        // 分割してダウンロード
        case OutputType.split:
          this.images[order[index]] = dataURL;
          break;
        // 選択したものを分割してダウンロード
        case OutputType.splitBySelect:
          this.images[order[this.outputStyle.selectFrames[index]]] = dataURL;
          break;
      }
    }
    if (this.outputStyle?.outputType === OutputType.splitBySelect) {
      for (let i = 0; i < 4; i++) {
        if (this.images[i] === undefined) {
          this.images[i] = notDownload;
        }
      }
    }
  };
}

export default defineComponent({
  name: "EditDownload",
  components: {
    EditDownloadSelectSize,
    EditDownloadSelectOutput,
    EditDownloadPreview,
  },
  props: {
    isShowModal: { type: Boolean },
    editStore: { type: Object as PropType<EditStore> },
    images: { type: Object as PropType<ImageFrame[]> },
  },
  setup(props, context) {
    const isShowModalInner = computed({
      get: () => props.isShowModal,
      set: (newValue) => {
        context.emit("update:isShowModal", newValue);
      },
    });

    // 各ダウンロードサイズ、形式の初期値を生成
    const makeInitialState = () => ({
      size: {
        sizeMode: SizeMode.FB,
        imgSize: { width: consts.fbSize.width, height: consts.fbSize.height },
      } as IDownloadSize,
      outputStyle: {
        outputType: OutputType.vAlign,
        selectFrames: [0, 1, 2, 3],
        twoBytwoOption: {
          order: ViewOrder.RTLB, // 右上、右下、左上、左下
          displayOrder: DisplayOrder.none,
          orderStyle: OrderStyle.type1,
        },
      } as IOutputStyle,
    });
    const state = reactive(makeInitialState());
    const page1 = Pages.Step1;
    const page2 = Pages.Step2;
    const currentPage = ref(page1);
    const isShowPreview = ref(false);
    const imagePreview = new ImagePreview();
    let isDownload = true;
    onBeforeUpdate(() => {
      currentPage.value = page1;
      const init = makeInitialState();
      state.size = init.size;
      state.outputStyle = init.outputStyle;
      imagePreview.init();
      isDownload = true;
    });

    const downloader = useDownloader();
    const loading = useLoading();
    const makeDataURIs = async () => {
      if (state.outputStyle === undefined) return;
      if (props.images === undefined) return;
      if (props.editStore === undefined) return;
      let isPreview = imagePreview.isSame(state.size, state.outputStyle);
      if (!isPreview) {
        // 方式とサイズにしたがって画像化
        const editedImgs = await flatPromise(
          props.images.map((x) => x.edited())
        );
        state.outputStyle.selectFrames.sort((a, b) => a - b);
        const dataURIs = await downloader.toImage(
          props.editStore.state.editData.title,
          await editedImgs,
          state.outputStyle,
          state.size.imgSize
        );
        // プレビュー画像保存
        imagePreview.set(state.size, state.outputStyle, dataURIs);
      }
      return isPreview;
    };
    const download = () => {
      loading.loadWith(async () => {
        const isPreview = await makeDataURIs();
        let dataURIs;
        if (isPreview) {
          // ダウンロードファイル名を現在日時に変更する
          if (props.editStore === undefined) return;
          dataURIs = downloader.replaceFileName(
            props.editStore.state.editData.title,
            state.outputStyle,
            imagePreview.dataURIs
          );
        } else {
          dataURIs = imagePreview.dataURIs;
        }

        // ダウンロード実行
        for (const [filename, dataURI] of dataURIs) {
          downloader.downloadURI(dataURI, filename);
        }
        isShowModalInner.value = false;
      });
    };
    const preview = () => {
      loading.loadWith(async () => {
        await makeDataURIs();
        isShowPreview.value = true;
      });
    };

    // エンター押下時の動作
    const enterAction = async () => {
      if (currentPage.value == page1) {
        // 1ページ目なら2ページ目へ異動
        currentPage.value = page2;
      } else {
        if (isShowPreview.value) {
          // プレビュー画面表示はenterActionが2回呼び出されるため、
          // プレビュー画面を閉じて、2回目の呼び出し時のダウンロードを抑止する
          isShowPreview.value = false;
          isDownload = false;
        } else {
          if (isDownload) {
            // 2ページ目ならダウンロード処理
            await download();
          } else {
            isDownload = true;
          }
        }
      }
    };

    return {
      state,
      isShowModalInner,
      page1,
      page2,
      currentPage,
      download,
      enterAction,
      isShowPreview,
      imagePreview,
      preview,
    };
  },
});
